import * as z from "zod";
import type { Transaction } from "@rocicorp/zero";
import { dropConnectorUsage } from "../connectorUsages";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";

export const deleteBranch = mutator({
  schema: z.object({ branchId: z.string() }),
  resolve: {
    branch: { kind: "branch", from: "branchId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.branch.workflow.spaceId,
    },
  ],
  fn: async ({ tx, args: { branch } }) => {
    await deleteBranchInTransaction(tx, branch);
  },
});

export async function deleteBranchInTransaction(
  tx: Transaction<Schema>,
  branch: { id: string; workflowId: string }
) {
  // Clear autofixBranchId/autoImproveBranchId if this was that AI branch
  const [workflow] = await tx.run(zql.workflows.where("id", branch.workflowId));
  if (workflow?.autofixBranchId === branch.id) {
    await tx.mutate.workflows.update({
      id: workflow.id,
      autofixBranchId: null,
    });
  }
  if (workflow?.autoImproveBranchId === branch.id) {
    await tx.mutate.workflows.update({
      id: workflow.id,
      autoImproveBranchId: null,
    });
  }

  const chatUsages = await tx.run(
    zql.connectedAppUsages
      .where("type", "branch_chat")
      .where("refId", branch.id)
  );
  for (const usage of chatUsages) {
    await dropConnectorUsage(tx, usage.connectedAppId, usage);
  }

  // Cascades will handle commits, history items, and chats
  await tx.mutate.workflowBranches.delete({ id: branch.id });

  const remaining = await tx.run(
    zql.workflowBranches.where("workflowId", branch.workflowId)
  );
  if (remaining.length === 0) {
    await tx.mutate.workflows.delete({ id: branch.workflowId });
  }
}
